// src/lib/stripe.js
// Client-side wrapper for Stripe operations. All calls route through the
// /.netlify/functions/stripeProxy function to keep the secret key server-side.
// Mirrors the signwell.js / smartPricingTable.js / hubspot.js patterns.

import { supabase } from './supabase';

async function stripeCall(action, payload = {}) {
  const res = await fetch('/.netlify/functions/stripeProxy', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ action, payload }),
  });
  const data = await res.json();
  if (!res.ok) {
    throw new Error(data.error || data.stripe_error?.message || `Stripe error ${res.status}`);
  }
  return data;
}

// ── Test connection ──────────────────────────────────────────────────────────
export async function testStripeConnection() {
  return stripeCall('testConnection');
}

// ── Create or refresh a Checkout Session for a quote prepayment ──────────────
// 1. Reads payment_cc_surcharge from pricing_settings (default 0.02)
// 2. Computes face + surcharge cents
// 3. Inserts (or updates) a stripe_payments row with status=pending
// 4. Calls stripeProxy to create a hosted Checkout Session
// 5. Returns the URL to email to the client
export async function createPrepaymentSession({
  quoteId,
  quoteType,           // 'managed-it' | 'multi-site-managed-it' | 'voice' | 'bundle' | 'flex'
  quoteNumber,
  clientName,
  clientEmail,
  prepayAmount,        // dollars (face value, ACH price)
  hubspotDealId,       // optional
  resendOfPaymentId,   // optional — if provided, refresh that row instead of inserting new
}) {
  if (!clientEmail)               throw new Error('Client email is required');
  if (!prepayAmount || prepayAmount <= 0) throw new Error('Prepayment amount must be greater than zero');

  // 1. Pull surcharge rate from pricing_settings (admin-configurable)
  const { data: surchargeRow } = await supabase
    .from('pricing_settings')
    .select('value')
    .eq('key', 'payment_cc_surcharge')
    .single();
  const surchargePct = parseFloat(surchargeRow?.value || '0.02');

  // 2. Compute cents (round to nearest cent on each leg, then total)
  const faceAmountCents      = Math.round(prepayAmount * 100);
  const surchargeAmountCents = Math.round(prepayAmount * surchargePct * 100);
  const totalChargedCents    = faceAmountCents + surchargeAmountCents;

  // 3. Get current user for created_by
  const { data: { user } } = await supabase.auth.getUser();

  // Determine current Stripe mode (just for tagging the row — proxy uses same source)
  const { data: modeRow } = await supabase
    .from('pricing_settings')
    .select('value')
    .eq('key', 'stripe_mode')
    .single();
  const mode = modeRow?.value || 'test';

  // 4. Insert or update stripe_payments row
  let paymentRow;
  if (resendOfPaymentId) {
    // Read current resend_count, then increment client-side
    const { data: existing } = await supabase
      .from('stripe_payments')
      .select('resend_count')
      .eq('id', resendOfPaymentId)
      .single();
    const nextCount = (existing?.resend_count || 0) + 1;

    const { data, error } = await supabase
      .from('stripe_payments')
      .update({
        face_amount_cents:      faceAmountCents,
        surcharge_pct:          surchargePct,
        surcharge_amount_cents: surchargeAmountCents,
        total_charged_cents:    totalChargedCents,
        client_email:           clientEmail,
        client_name:            clientName,
        status:                 'pending',
        resend_count:           nextCount,
        last_resent_at:         new Date().toISOString(),
        failure_reason:         null,
      })
      .eq('id', resendOfPaymentId)
      .select()
      .single();
    if (error) throw new Error(`Failed to update payment row: ${error.message}`);
    paymentRow = data;
  } else {
    const { data, error } = await supabase
      .from('stripe_payments')
      .insert({
        quote_id:               quoteId,
        quote_type:             quoteType,
        quote_number:           quoteNumber,
        client_name:            clientName,
        client_email:           clientEmail,
        face_amount_cents:      faceAmountCents,
        surcharge_pct:          surchargePct,
        surcharge_amount_cents: surchargeAmountCents,
        total_charged_cents:    totalChargedCents,
        mode,
        status:                 'pending',
        hubspot_deal_id:        hubspotDealId || null,
        created_by:             user?.id || null,
      })
      .select()
      .single();
    if (error) throw new Error(`Failed to create payment row: ${error.message}`);
    paymentRow = data;
  }

  // 5. Build success/cancel URLs — public receipt page
  const origin = window.location.origin;
  const successUrl = `${origin}/pay/{CHECKOUT_SESSION_ID}?status=success`;
  const cancelUrl  = `${origin}/pay/{CHECKOUT_SESSION_ID}?status=cancelled`;

  // 6. Create the Stripe Checkout Session via the proxy
  const session = await stripeCall('createCheckoutSession', {
    faceAmountCents,
    surchargeAmountCents,
    surchargePct,
    clientName,
    clientEmail,
    quoteNumber,
    quoteId,
    quoteType,
    paymentRowId: paymentRow.id,
    successUrl,
    cancelUrl,
  });

  // 7. Persist the session details on the row
  const expiresAt = session.expires_at ? new Date(session.expires_at * 1000).toISOString() : null;
  const { data: updated, error: upErr } = await supabase
    .from('stripe_payments')
    .update({
      stripe_session_id: session.id,
      checkout_url:      session.url,
      expires_at:        expiresAt,
    })
    .eq('id', paymentRow.id)
    .select()
    .single();
  if (upErr) console.warn('Failed to persist session details:', upErr.message);

  return {
    paymentId:  paymentRow.id,
    sessionId:  session.id,
    url:        session.url,
    expiresAt,
    faceAmountCents,
    surchargeAmountCents,
    totalChargedCents,
    surchargePct,
    mode:       session.mode || mode,
  };
}

// ── Get latest payment for a quote ───────────────────────────────────────────
export async function getLatestPaymentForQuote(quoteId) {
  if (!quoteId) return null;
  const { data, error } = await supabase
    .from('stripe_payments')
    .select('*')
    .eq('quote_id', quoteId)
    .order('created_at', { ascending: false })
    .limit(1)
    .maybeSingle();
  if (error) {
    console.warn('Failed to load payment:', error.message);
    return null;
  }
  return data;
}

// ── Get payment by stripe_session_id (used by public receipt page) ───────────
export async function getPaymentBySessionId(sessionId) {
  if (!sessionId) return null;
  const { data, error } = await supabase
    .from('stripe_payments')
    .select('*')
    .eq('stripe_session_id', sessionId)
    .maybeSingle();
  if (error) {
    console.warn('Failed to load payment by session:', error.message);
    return null;
  }
  return data;
}

// ── Refresh a payment from Stripe (sync DB with Stripe's truth) ──────────────
export async function refreshPaymentFromStripe(sessionId) {
  if (!sessionId) throw new Error('sessionId required');
  return stripeCall('getCheckoutSession', { sessionId });
}

// ── Expire (cancel) a pending checkout session ───────────────────────────────
export async function expireCheckoutSession(sessionId) {
  if (!sessionId) throw new Error('sessionId required');
  const result = await stripeCall('expireCheckoutSession', { sessionId });
  // Mark our DB row cancelled too
  await supabase
    .from('stripe_payments')
    .update({ status: 'cancelled' })
    .eq('stripe_session_id', sessionId)
    .eq('status', 'pending');
  return result;
}

// ── Format helpers ───────────────────────────────────────────────────────────
export const fmtCents = (c) => c == null ? '—' : `$${(c / 100).toFixed(2)}`;
export const fmtCentsWhole = (c) => c == null ? '—' : `$${Math.round(c / 100).toLocaleString()}`;

// ── Email the Checkout link to the client ────────────────────────────────────
// v3.5.51: rewritten to use the action/payload contract that sendEmail.js
// expects. Previously this called sendEmail with `{to, subject, html}` and
// got 400 "Unknown action: undefined" back — every Resend Email click and
// every webhook auto-send has been silently failing since v3.5.18 introduced
// this flow. The HTML is now rendered server-side in sendEmail.js's
// `payment_link` action, eliminating the duplicate template that previously
// lived here AND in netlify/functions/signwellWebhook.js.
export async function emailPaymentLink({
  paymentId,
  quoteNumber,
  clientName,
  clientEmail,
  recipientContact,
  checkoutUrl,
  faceAmountCents,
  surchargeAmountCents,
  totalChargedCents,
  surchargePct,
  customMessage,
}) {
  const res = await fetch('/.netlify/functions/sendEmail', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      action: 'payment_link',
      payload: {
        clientEmail,
        clientName,
        recipientContact,
        quoteNumber,
        checkoutUrl,
        faceAmountCents,
        surchargeAmountCents,
        totalChargedCents,
        surchargePct,
        customMessage,
        context: 'manual_resend',
      },
    }),
  });

  // v3.5.52: capture and surface the actual error from sendEmail.js so the
  // UI can show meaningful diagnostic info instead of a generic "Email
  // failed". The function returns { error, detail } on non-2xx.
  let errorMessage = null;
  if (!res.ok) {
    try {
      const errBody = await res.json();
      errorMessage = errBody?.error || `HTTP ${res.status}`;
      // SMTP2Go errors land in detail; surface the most informative bit
      if (errBody?.detail?.data?.error) errorMessage += `: ${errBody.detail.data.error}`;
      else if (errBody?.detail?.data?.error_code) errorMessage += `: ${errBody.detail.data.error_code}`;
      else if (errBody?.detail?.error) errorMessage += `: ${errBody.detail.error}`;
      else if (errBody?.detail) errorMessage += ` (${JSON.stringify(errBody.detail).slice(0, 200)})`;
    } catch {
      errorMessage = `HTTP ${res.status}`;
    }
  }

  const ok = res.ok;
  const sentAt = new Date().toISOString();

  if (ok && paymentId) {
    await supabase
      .from('stripe_payments')
      .update({ email_sent_at: sentAt, email_sent_to: clientEmail })
      .eq('id', paymentId);
  }

  return { ok, sentAt, error: errorMessage };
}
